/** Shared constants and UI for WhatsApp template mapping (settings + template library). */

export const WHATSAPP_SETTINGS_COMPANY_KEY = 'whatsapp_settings_company_id';

export function persistWhatsAppCompanyId(companyId) {
  if (companyId == null) return;
  try {
    sessionStorage.setItem(WHATSAPP_SETTINGS_COMPANY_KEY, String(companyId));
  } catch {
    // ignore
  }
}

export function clearWhatsAppCompanyId() {
  try {
    sessionStorage.removeItem(WHATSAPP_SETTINGS_COMPANY_KEY);
  } catch {
    // ignore
  }
}

export function readWhatsAppCompanyId() {
  try {
    const raw = sessionStorage.getItem(WHATSAPP_SETTINGS_COMPANY_KEY);
    if (raw) {
      const id = parseInt(raw, 10);
      if (!Number.isNaN(id)) return id;
    }
  } catch {
    // ignore
  }
  return null;
}

/** E.164 input: leading +, then up to 14 digits (15 characters total). */
export function sanitizeE164PhoneInput(raw) {
  const stripped = String(raw || '').replace(/[^\d+]/g, '');
  const digits = stripped.replace(/\+/g, '').slice(0, 14);
  return digits ? `+${digits}` : stripped.startsWith('+') ? '+' : '';
}

export function isValidE164Phone(phone) {
  return /^\+\d{1,14}$/.test(String(phone || '').trim());
}

function credentialPresent(block, key) {
  if (!block) return false;
  if (block[`${key}_configured`]) return true;
  const value = block[key];
  return value != null && String(value).trim() !== '';
}

/** True when provider + credentials exist (saved on server or filled in the wizard). */
export function hasProviderConfigured(data) {
  if (!data?.active_provider) return false;
  const pid = data.active_provider;
  const block = data.providers?.[pid] || {};
  if (pid === 'twilio') {
    return (
      credentialPresent(block, 'account_sid') &&
      credentialPresent(block, 'from_number') &&
      credentialPresent(block, 'auth_token')
    );
  }
  if (pid === 'meta_cloud') {
    return credentialPresent(block, 'phone_number_id') && credentialPresent(block, 'access_token');
  }
  if (pid === '360dialog') {
    return credentialPresent(block, 'phone_number_id') && credentialPresent(block, 'api_key');
  }
  if (pid === 'interakt') {
    return (
      credentialPresent(block, 'api_key') && isValidE164Phone(data.sender_phone_e164)
    );
  }
  return false;
}

export const CANONICAL_EVENTS = [
  { id: 'interview_invite', labelKey: 'whatsappSettings.events.interviewInvite' },
  { id: 'interview_reminder', labelKey: 'whatsappSettings.events.interviewReminder' },
  { id: 'interview_expiry', labelKey: 'whatsappSettings.events.interviewExpiry' },
  { id: 'interview_reschedule', labelKey: 'whatsappSettings.events.interviewReschedule' },
  { id: 'candidate_selection', labelKey: 'whatsappSettings.events.candidateSelection' },
  { id: 'candidate_rejection', labelKey: 'whatsappSettings.events.candidateRejection' },
  { id: 'recruitment_update', labelKey: 'whatsappSettings.events.recruitmentUpdate' },
];

export const META_BODY_ORDER = [
  'candidate_name',
  'job_title',
  'company_name',
  'interview_url',
  'days_remaining',
  'message_body',
];

export const EVENT_PAYLOAD_KEYS = {
  interview_invite: ['candidate_name', 'job_title', 'company_name', 'interview_url'],
  interview_reschedule: ['candidate_name', 'job_title', 'company_name', 'interview_url'],
  interview_reminder: [
    'candidate_name',
    'job_title',
    'company_name',
    'interview_url',
    'days_remaining',
    'urgency_message',
    'time_message',
  ],
  interview_expiry: ['candidate_name', 'job_title', 'company_name'],
  candidate_selection: ['candidate_name', 'job_title', 'company_name'],
  candidate_rejection: ['candidate_name', 'job_title', 'company_name'],
  recruitment_update: ['message_body'],
};

const PROVIDER_LABELS = {
  meta_cloud: 'Meta Cloud API',
  twilio: 'Twilio',
  '360dialog': '360dialog',
  interakt: 'Interakt',
};

export function getProviderLabel(id) {
  return PROVIDER_LABELS[id] || id;
}

export function getMetaBodyKeysForEvent(eventId) {
  const keys = EVENT_PAYLOAD_KEYS[eventId] || [];
  return META_BODY_ORDER.filter((k) => keys.includes(k));
}

export function usesMetaStylePlaceholders(providerId) {
  return providerId === 'meta_cloud' || providerId === '360dialog';
}

export function TemplatePayloadCaption({ eventId, providerId, t }) {
  const payloadKeys = EVENT_PAYLOAD_KEYS[eventId] || [];
  const metaBodyKeys = getMetaBodyKeysForEvent(eventId);
  const showMetaIndex = usesMetaStylePlaceholders(providerId);

  if (!payloadKeys.length) return null;

  return (
    <div className="mb-3 rounded-lg bg-white border border-blue-100/80 px-3 py-2.5">
      <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2">
        {t('whatsappSettings.payloadCaption.title')}
      </p>
      <ul className="space-y-1.5">
        {payloadKeys.map((key) => {
          const metaIndex = metaBodyKeys.indexOf(key);
          const inMetaBody = metaIndex >= 0;
          const twilioOnly = !inMetaBody;
          return (
            <li key={key} className="text-xs leading-snug">
              <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                {showMetaIndex && inMetaBody && (
                  <span className="font-mono text-[11px] font-bold text-[#0284c7] bg-blue-50 px-1.5 py-0.5 rounded">
                    {`{{${metaIndex + 1}}}`}
                  </span>
                )}
                {showMetaIndex && twilioOnly && (
                  <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 px-1 rounded">
                    {t('whatsappSettings.payloadCaption.twilioOnly')}
                  </span>
                )}
                <span className="font-bold text-gray-800">
                  {t(`whatsappSettings.payloadFields.${key}.label`)}
                </span>
                <span className="font-mono text-[10px] text-gray-400">({key})</span>
              </div>
              <p className="text-gray-500 mt-0.5">
                {t(`whatsappSettings.payloadFields.${key}.description`)}
              </p>
            </li>
          );
        })}
      </ul>
      {showMetaIndex && metaBodyKeys.length > 0 && (
        <p className="text-[10px] text-gray-500 mt-2 pt-2 border-t border-gray-100">
          {t('whatsappSettings.payloadCaption.metaOrderNote', {
            placeholders: metaBodyKeys.map((_, i) => `{{${i + 1}}}`).join(', '),
          })}
        </p>
      )}
    </div>
  );
}
